/*************************************************

* BAKER HUGHES COMPANY CONFIDENTIAL

* Unpublished work copyright 2025 Baker Hughes Company.  All rights reserved.

*

* This computer code is proprietary and highly confidential to Baker Hughes Company

* and/or its affiliate(s). It may not be used, disclosed, modified, transferred,

* or reproduced without prior written consent, and must be returned on demand.

**************************************************/
/* copied from Backstage repo plugins\catalog\src\components\AboutCard\AboutContent.tsx and customized */

import {
  Entity,
  getEntitySourceLocation,
  RELATION_OWNED_BY,
  RELATION_PART_OF,
} from '@backstage/catalog-model';
import {
  EntityRefLinks,
  getEntityRelations,
} from '@backstage/plugin-catalog-react';
import { JsonArray, JsonValue } from '@backstage/types';
import { Chip, Grid, makeStyles } from '@material-ui/core';
import { MarkdownContent } from '@backstage/core-components';

import { AboutField } from '@backstage/plugin-catalog';
import { LinksGridList } from '../../EntityLinksCard/LinksGridList';

const useStyles = makeStyles({
  description: {
    wordBreak: 'break-word',
  },
});

/**
 * Props for {@link AboutContent}.
 *
 * @public
 */
export interface AboutContentProps {
  entity: Entity;
}

type EntitySourceLocation =
  | {
      type: string;
      target: string;
    }
  | undefined;

function getLocationTargetHref(
  target: string,
  type: string,
  entitySourceLocation: {
    type: string;
    target: string;
  },
): string {
  if (type === 'url' || target.includes('://')) {
    return target;
  }

  const srcLocationUrl =
    entitySourceLocation.type === 'file'
      ? `file://${entitySourceLocation.target}`
      : entitySourceLocation.target;

  if (type === 'file' || entitySourceLocation.type === 'file') {
    return new URL(target, srcLocationUrl).href;
  }

  return srcLocationUrl;
}

// LinksGridField with value prop
const LinksGridField = ({
  isLocation,
  entity,
  entitySourceLocation,
}: {
  isLocation: boolean;
  entity: Entity;
  entitySourceLocation: EntitySourceLocation;
}) => {
  let targets: string[] = [];
  if (Array.isArray(entity?.spec?.targets)) {
    // Filter to strings only to be safe
    targets = (entity.spec?.targets as JsonArray).filter(
      t => typeof t === 'string',
    ) as string[];
  } else if (typeof entity?.spec?.target === 'string' && entity.spec.target) {
    targets = [entity.spec.target];
  }

  if (!isLocation || targets.length === 0) {
    return null;
  }

  const items = targets.map(t => ({
    text: t,
    href: getLocationTargetHref(
      t,
      (entity?.spec?.type || 'unknown') as string,
      entitySourceLocation &&
        typeof entitySourceLocation === 'object' &&
        'type' in entitySourceLocation &&
        'target' in entitySourceLocation
        ? entitySourceLocation
        : { type: 'unknown', target: 'unknown' }, // Fallback value
    ),
  }));

  return (
    <AboutField
      label="Targets"
      value={targets.join(', ')} // makes .nextSibling contain the URLs
      gridSizes={{ xs: 12 }}
    >
      <LinksGridList cols={1} items={items} />
    </AboutField>
  );
};

// const ReleaseField = (props: {
//   isAPI: boolean;
//   isComponent: boolean;
//   release: JsonValue | undefined | null;
// }) => {
//   const { isAPI, isComponent, release } = props;
//   return isAPI || isComponent || typeof release === 'string' ? (
//     <AboutField
//       label="Release"
//       value={release as string}
//       gridSizes={{ xs: 12, sm: 6, lg: 4 }}
//     />
//   ) : null;
// };

const VersionField = ({
  manifests,
}: {
  manifests?: Array<{ tags: string[]; lastUpdatedOn: string }>;
}) => {
  if (!manifests || manifests.length === 0) {
    return null;
  }

  // Find the latest manifest based on the `lastUpdatedOn` field
  const latestManifest = manifests
    .slice() // Create a shallow copy to avoid mutating the original array
    .sort(
      (a, b) =>
        new Date(b.lastUpdatedOn).getTime() -
        new Date(a.lastUpdatedOn).getTime(),
    )[0];

  const latestVersion = latestManifest?.tags?.[0] || 'No Version';

  return (
    <AboutField
      label="Version"
      value={latestVersion}
      gridSizes={{ xs: 12, sm: 6, lg: 4 }}
    />
  );
};

const LastUpdatedField = ({
  manifests,
}: {
  manifests?: Array<{ tags: string[]; lastUpdatedOn: string }>;
}) => {
  if (!manifests || manifests.length === 0) {
    return null;
  }

  // Find the latest manifest based on the `lastUpdatedOn` field
  const latestManifest = manifests
    .slice() // Create a shallow copy to avoid mutating the original array
    .sort(
      (a, b) =>
        new Date(b.lastUpdatedOn).getTime() -
        new Date(a.lastUpdatedOn).getTime(),
    )[0];

  const lastUpdatedOn = latestManifest?.lastUpdatedOn
    ? new Date(latestManifest.lastUpdatedOn).toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      })
    : 'Not Available';

  return (
    <AboutField
      label="Last Updated On"
      value={lastUpdatedOn}
      gridSizes={{ xs: 12, sm: 6, lg: 4 }}
    />
  );
};

const ChartAppVersionField = (props: {
  chartAppVersion: JsonValue | undefined | null;
}) => {
  const { chartAppVersion } = props;
  if (!chartAppVersion) {
    return null;
  }
  return chartAppVersion ? (
    <AboutField
      label="Chart App Version"
      value={chartAppVersion as string}
      gridSizes={{ xs: 12, sm: 6, lg: 4 }}
    />
  ) : null;
};

const ToolsVersionField = (props: {
  toolVersion: JsonValue | undefined | null;
}) => {
  const { toolVersion } = props;
  if (!toolVersion) {
    return null;
  }
  return toolVersion ? (
    <AboutField
      label="Tool Version"
      value={toolVersion as string}
      gridSizes={{ xs: 12, sm: 6, lg: 4 }}
    />
  ) : null;
};

/** @public */
export function InfraAboutContent(props: AboutContentProps) {
  const { entity } = props;
  const classes = useStyles();
  const isSystem = entity.kind.toLocaleLowerCase('en-US') === 'system';
  const isResource = entity.kind.toLocaleLowerCase('en-US') === 'resource';
  const isComponent = entity.kind.toLocaleLowerCase('en-US') === 'component';
  const isAPI = entity.kind.toLocaleLowerCase('en-US') === 'api';
  const isTemplate = entity.kind.toLocaleLowerCase('en-US') === 'template';
  const isLocation = entity.kind.toLocaleLowerCase('en-US') === 'location';
  const isGroup = entity.kind.toLocaleLowerCase('en-US') === 'group';

  // const partOfSystemRelations = getEntityRelations(entity, RELATION_PART_OF, {
  //   kind: 'system',
  // });
  const partOfComponentRelations = getEntityRelations(
    entity,
    RELATION_PART_OF,
    {
      kind: 'component',
    },
  );
  const partOfDomainRelations = getEntityRelations(entity, RELATION_PART_OF, {
    kind: 'domain',
  });
  const ownedByRelations = getEntityRelations(entity, RELATION_OWNED_BY);

  let entitySourceLocation:
    | {
        type: string;
        target: string;
      }
    | undefined;
  try {
    entitySourceLocation = getEntitySourceLocation(entity);
  } catch (e) {
    entitySourceLocation = undefined;
  }

  const manifests = Array.isArray(entity?.spec?.manifests)
    ? (entity?.spec?.manifests as { tags: string[]; lastUpdatedOn: string }[])
    : undefined;

  // Resolve description once with optional chaining and trimming
  const descriptionText = entity?.metadata?.description?.trim() ?? 'No description';

  return (
    <Grid container>
      <AboutField
        label="Description"
        value={descriptionText}
        gridSizes={{ xs: 12 }}
      >
        <MarkdownContent
          className={classes.description}
          content={descriptionText}
        />
      </AboutField>

      <AboutField
        label="Owner"
        value={ownedByRelations.length > 0 ? 'user:guest' : 'No Owner'}
        gridSizes={{ xs: 12, sm: 6, lg: 4 }}
      >
        {ownedByRelations.length > 0 && (
          <EntityRefLinks entityRefs={ownedByRelations} defaultKind="group" />
        )}
      </AboutField>
      {(isSystem || partOfDomainRelations.length > 0) && (
        <AboutField
          label="Domain"
          value="No Domain"
          gridSizes={{ xs: 12, sm: 6, lg: 4 }}
        >
          {partOfDomainRelations.length > 0 && (
            <EntityRefLinks
              entityRefs={partOfDomainRelations}
              defaultKind="domain"
            />
          )}
        </AboutField>
      )}
      {/* {(isAPI ||
          isComponent ||
          isResource ||
          partOfSystemRelations.length > 0) && (
          <AboutField
            label="System"
            value="No System"
            gridSizes={{ xs: 12, sm: 6, lg: 4 }}
          >
            {partOfSystemRelations.length > 0 && (
              <EntityRefLinks
                entityRefs={partOfSystemRelations}
                defaultKind="system"
              />
            )}
          </AboutField>
        )} */}
      {isComponent && partOfComponentRelations.length > 0 && (
        <AboutField
          label="Parent Component"
          value="No Parent Component"
          gridSizes={{ xs: 12, sm: 6, lg: 4 }}
        >
          <EntityRefLinks
            entityRefs={partOfComponentRelations}
            defaultKind="component"
          />
        </AboutField>
      )}
      {(isAPI ||
        isComponent ||
        isResource ||
        isTemplate ||
        isGroup ||
        isLocation ||
        typeof entity?.spec?.type === 'string') && (
        <AboutField
          label="Type"
          value={entity?.spec?.type as string}
          gridSizes={{ xs: 12, sm: 6, lg: 4 }}
        />
      )}
      {/* <ReleaseField
          isAPI
          isComponent
          release={entity?.metadata?.currentRelease}
        /> */}
      <VersionField manifests={manifests} />
      <LastUpdatedField manifests={manifests} />
      <ChartAppVersionField chartAppVersion={entity?.spec?.chartAppVersion} />
      {entity.spec?.type === 'tools' && (
        <ToolsVersionField toolVersion={entity?.spec?.version} />
      )}
      <AboutField
        label="Tags"
        value="No Tags"
        gridSizes={{ xs: 12, sm: 6, lg: 4 }}
      >
        {(entity?.metadata?.tags || []).map(t => (
          <Chip key={t} size="small" label={t} />
        ))}
      </AboutField>
      <LinksGridField
        entity={entity}
        entitySourceLocation={entitySourceLocation}
        isLocation={isLocation}
      />
    </Grid>
  );
}
